import apiClient, { type PagedResponse } from '../client'
import type {
  RecepcionListItemResponse,
  ValidacionConfigResponse,
  IniciarRecepcionRequest,
  IniciarRecepcionResponse,
  ValidarItemResponse,
  ResultadoRecepcionResponse,
  DetalleValidacionResponse,
  ActualizarManualRequest,
  FinalizarRecepcionRequest,
  FinalizarRecepcionResponse,
  DetalleRecepcionResponse,
  LoteValidarQrResponse,
  GenerateSamplesRequest,
  GenerateSamplesResponse,
} from '../models/recepcion.models'

const BASE_PATH = '/recepciones'

export const recepcionesService = {
  async getAll(params?: {
    page?: number
    pageSize?: number
    estado?: number
    busqueda?: string
  }): Promise<PagedResponse<RecepcionListItemResponse>> {
    return apiClient.get<PagedResponse<RecepcionListItemResponse>>(BASE_PATH, params)
  },

  async getValidacionConfig(ruc: string): Promise<ValidacionConfigResponse> {
    return apiClient.get<ValidacionConfigResponse>(`${BASE_PATH}/validaciones/config`, { ruc })
  },

  async iniciar(data: IniciarRecepcionRequest): Promise<IniciarRecepcionResponse> {
    return apiClient.post<IniciarRecepcionResponse, IniciarRecepcionRequest>(
      `${BASE_PATH}/iniciar`,
      data,
    )
  },

  async validarItem(recepcionId: string, validacionConfigId: string): Promise<ValidarItemResponse> {
    return apiClient.post<ValidarItemResponse>(
      `${BASE_PATH}/${recepcionId}/validar/${validacionConfigId}`,
    )
  },

  async getResultado(recepcionId: string): Promise<ResultadoRecepcionResponse> {
    return apiClient.get<ResultadoRecepcionResponse>(`${BASE_PATH}/${recepcionId}/resultado`)
  },

  async getDetalleValidacion(
    recepcionId: string,
    validacionId: string,
  ): Promise<DetalleValidacionResponse> {
    return apiClient.get<DetalleValidacionResponse>(
      `${BASE_PATH}/${recepcionId}/validaciones/${validacionId}`,
    )
  },

  async actualizarManual(
    recepcionId: string,
    validacionId: string,
    data: ActualizarManualRequest,
  ): Promise<void> {
    return apiClient.put<void, ActualizarManualRequest>(
      `${BASE_PATH}/${recepcionId}/validaciones/${validacionId}/manual`,
      data,
    )
  },

  async finalizar(
    recepcionId: string,
    data: FinalizarRecepcionRequest,
  ): Promise<FinalizarRecepcionResponse> {
    return apiClient.post<FinalizarRecepcionResponse, FinalizarRecepcionRequest>(
      `${BASE_PATH}/${recepcionId}/finalizar`,
      data,
    )
  },

  async getDetalle(recepcionId: string): Promise<DetalleRecepcionResponse> {
    return apiClient.get<DetalleRecepcionResponse>(`${BASE_PATH}/${recepcionId}`)
  },

  // Re-runs the verification report PDF generator. Useful for receptions that
  // were finalized before auto-generation was added, or when the operator wants
  // a fresh copy after editing manual validations.
  async generarVerificationReport(recepcionId: string): Promise<{ fileId: string; fileName: string; runNumber: number }> {
    return apiClient.post<{ fileId: string; fileName: string; runNumber: number }>(
      `${BASE_PATH}/${recepcionId}/verification-report`,
    )
  },

  async buscarLotePorCodigo(codigo: string): Promise<LoteValidarQrResponse> {
    return apiClient.get<LoteValidarQrResponse>('/lotes/validar-qr', { codigo })
  },

  // ── Muestreo ──
  async generarMuestreo(
    recepcionId: string,
    data: GenerateSamplesRequest = {},
  ): Promise<GenerateSamplesResponse> {
    return apiClient.post<GenerateSamplesResponse, GenerateSamplesRequest>(
      `${BASE_PATH}/${recepcionId}/muestreos`,
      data,
    )
  },

  async listarMuestras(recepcionId: string): Promise<GenerateSamplesResponse> {
    return apiClient.get<GenerateSamplesResponse>(`${BASE_PATH}/${recepcionId}/muestreos`)
  },

  async marcarMuestrasImpresas(recepcionId: string): Promise<boolean> {
    return apiClient.post<boolean>(`${BASE_PATH}/${recepcionId}/muestreos/imprimir`)
  },
}
